import { FLAGS } from '@iheartjane/feature-flags';
import type { CompareProduct } from '@iheartjane/menu';
import {
  AddToBagModal,
  MAX_COMPARE,
  ProductCard,
  useCompareActions,
} from '@iheartjane/menu';
import { useToast } from '@iheartjane/reefer';
import type { PriceId } from '@iheartjane/types';
import type { SyntheticEvent } from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { Form, useNavigate, useSubmit } from 'react-router';

import { BagTransferModal } from '@jane/bloom/components/bagTransferModal/bagTransferModal';
import { BloomLink } from '@jane/bloom/components/bloomLink';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { ProductCategoryLabel } from '@jane/bloom/components/productCategoryLabel/productCategoryLabel';
import { useMarketplaceCart } from '@jane/bloom/hooks/cart/useMarketplaceCart';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useAddToCartConfig } from '@jane/bloom/hooks/useAddToCartConfig';
import { useOptimisticBagSize } from '@jane/bloom/hooks/useBagSize';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import {
  trackAddedProductToCompare,
  trackClickedCompareModalProduct,
  trackProductListingTap,
} from '@jane/bloom/services/tracking/track';
import { mergeCartItem } from '@jane/bloom/utils/cart/mergeCartItem';
import { getMaxCartQuantity } from '@jane/bloom/utils/getMaxCartQuantity';
import { formatWeight } from '@jane/bloom/utils/strings/formatWeight';
import {
  buildProductCardProps,
  getDefaultWeight,
} from '@jane/bloom/viewModels/buildProductCardProps';
import { buildWeightOptions } from '@jane/bloom/viewModels/buildWeightOptions';
import { buildCompareProduct } from '@jane/bloom/viewModels/compareProduct';

import { MenuCompareToggle } from './menuCompareToggle';
import type { ProductCardProps } from './productCard';

import styles from './productCard.module.css';

export const MenuProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  function MenuProductCard(
    {
      brandSpecials,
      customRowId,
      isPlacement,
      isSponsored,
      maxWeightOption,
      preloaded,
      product,
      selected,
      smartProductInstance,
      weightFilter,
    },
    ref
  ) {
    const { fulfillmentMode, pinnedStore } = useStoreSwitcher();
    const { cart, featureFlags, shoppingPreferences, theme } =
      useRootLoaderData();
    const submit = useSubmit();
    const navigate = useNavigate();
    const toast = useToast();

    const defaultWeight = getDefaultWeight(product);
    const [modalSelectedWeight, setModalSelectedWeight] = useState<PriceId>();
    const {
      fetcher: { state },
      formArgs,
    } = useAddToCartConfig({
      product,
      productId: product.product_id,
      quantity: 1,
      rowId: customRowId,
      selectedWeight: modalSelectedWeight ?? defaultWeight,
    });
    const { createCart, deleteCart, newCart } = useMarketplaceCart();

    const isAddingToCart = state === 'submitting';
    const countInCart = useOptimisticBagSize(product.product_id);

    const [showWarningModal, setShowWarningModal] = useState(false);
    // { layered: true } = opened from compare modal, { layered: false } = opened from card, null = closed
    const [weightModal, setWeightModal] = useState<{ layered: boolean } | null>(
      null
    );

    const onSdkProductClick = useCallback(
      (addToCart = false) => {
        smartProductInstance?.click({ isAddToCart: addToCart });
      },
      [smartProductInstance]
    );

    const weights = useMemo(
      () =>
        buildWeightOptions({
          maxWeightOption,
          product,
          smartProductInstance,
          storeState: pinnedStore?.state,
          weightFilter,
        }),
      [
        maxWeightOption,
        pinnedStore?.state,
        product,
        smartProductInstance,
        weightFilter,
      ]
    );

    const disabledWeights = useMemo(() => {
      const disabled = new Set<PriceId>();
      for (const weight of weights) {
        const remaining = getMaxCartQuantity({
          cart,
          currentProduct: product,
          currentWeight: weight.value,
          storeId: shoppingPreferences?.storeId,
        });
        if (remaining === 0) {
          disabled.add(weight.value);
        }
      }
      return disabled;
    }, [weights, cart, product, shoppingPreferences?.storeId]);

    const atMaxQuantity = product.hasMultipleWeights
      ? weights.length > 0 && disabledWeights.size === weights.length
      : getMaxCartQuantity({
          cart,
          currentProduct: product,
          currentWeight: defaultWeight,
          storeId: shoppingPreferences?.storeId,
        }) === 0;

    const cardProps = useMemo(
      () =>
        buildProductCardProps({
          atMaxQuantity,
          brandSpecials,
          cartCount: countInCart,
          isAddingToCart,
          isPlacement,
          isSponsored,
          preloaded,
          product,
          productReviewsEnabled: pinnedStore?.product_reviews_enabled,
          storeState: pinnedStore?.state,
        }),
      [
        atMaxQuantity,
        brandSpecials,
        countInCart,
        isAddingToCart,
        isPlacement,
        isSponsored,
        pinnedStore?.state,
        pinnedStore?.product_reviews_enabled,
        preloaded,
        product,
      ]
    );

    // if cart items in the cart are from a different from the current store, prevent the add to cart action and show warning modal
    const hasPreviousStoreProducts = cart.storeId
      ? cart.storeId !== shoppingPreferences?.storeId
      : false;

    const stageBagTransfer = useCallback(
      (item: { count: number; priceId: string; productId: number }) => {
        if (shoppingPreferences) {
          createCart({
            products: mergeCartItem(
              {
                count: item.count,
                price_id: formatWeight(item.priceId),
                product_id: item.productId,
              },
              cart.items
            ),
            reservationMode: shoppingPreferences.reservationMode,
            storeId: shoppingPreferences.storeId,
          });
        }

        setShowWarningModal(true);
      },
      [shoppingPreferences, createCart, cart.items]
    );

    const handleSingleWeightAddToBag = (
      event: SyntheticEvent<HTMLFormElement, SubmitEvent>
    ) => {
      onSdkProductClick(true);

      if (hasPreviousStoreProducts) {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        const priceId = formData.get('priceId');
        stageBagTransfer({
          count: Number(formData.get('count')),
          priceId: priceId ? String(priceId) : 'each',
          productId: Number(formData.get('productId')),
        });
      }
    };

    const confirmStartNewBag = () => {
      setShowWarningModal(false);

      // replace the current cart with the new cart
      if (newCart) {
        submit({ shouldReplaceCart: 'true' }, formArgs);
      }
    };

    const handleCartWarningClose = () => {
      setShowWarningModal(false);

      // delete the pending cart
      if (newCart) {
        deleteCart({ cartUuid: newCart?.cart.uuid });
      }
    };

    const handleMultiWeightAddToBag = useCallback(
      (weight: PriceId) => {
        // Only compare-initiated adds toast
        const fromCompare = weightModal?.layered ?? false;
        onSdkProductClick(true);

        if (hasPreviousStoreProducts) {
          setWeightModal(null);
          stageBagTransfer({
            count: 1,
            priceId: weight,
            productId: product.id,
          });
          return;
        }

        submit({ count: 1, priceId: weight, productId: product.id }, formArgs);
        setWeightModal(null);
        if (fromCompare) {
          toast.add({ label: 'Added to bag', time: 2500 });
        }
      },
      [
        weightModal,
        onSdkProductClick,
        hasPreviousStoreProducts,
        stageBagTransfer,
        product.id,
        submit,
        formArgs,
        toast,
      ]
    );

    const onWeightChange = useCallback(
      (weight: PriceId) => {
        smartProductInstance?.click();
        setModalSelectedWeight(weight);
      },
      [smartProductInstance]
    );

    // --- Compare products ---
    const compareEnabled = featureFlags[FLAGS.c1CompareProducts];
    const {
      getIsSelected,
      getProductCount,
      setActive: setCompareActive,
      toggleProduct,
    } = useCompareActions();

    const compareProduct = useMemo<CompareProduct | null>(() => {
      if (!compareEnabled) return null;
      return buildCompareProduct({
        product,
        storeState: pinnedStore?.state,
      });
    }, [compareEnabled, product, pinnedStore?.state]);

    const handleCompareToggle = useCallback(() => {
      if (!compareProduct) return;

      const compareTrackingProps = {
        productBrand: product.brand,
        productId: product.product_id,
        productKind: product.kind,
        productName: product.name,
      };

      // The "View" link and product row both navigate to the PDP (via `url`),
      // so they map to a modal-product click — no add-to-cart there.
      const trackModalProductClick = () => {
        onSdkProductClick();
        trackClickedCompareModalProduct(compareTrackingProps);
      };

      // Pragmatic add from the compare modal: single-weight products with no
      // cross-store conflict add in one click. Multi-weight products open a
      // weight-selection modal stacked on top of the compare modal. Cross-store
      // carts need the BagTransferModal flow, which lives on the PDP, so they
      // fall back to opening the product page.
      const addFromCompareModal = () => {
        if (hasPreviousStoreProducts) {
          trackModalProductClick();
          if (compareProduct.url) navigate(compareProduct.url);
          return;
        }

        if (product.hasMultipleWeights) {
          onSdkProductClick(false); // DM click, no add-to-cart yet
          setWeightModal({ layered: true });
          return;
        }

        onSdkProductClick(true); // DM add-to-cart click
        submit(
          { count: 1, priceId: defaultWeight, productId: product.id },
          formArgs
        );
        toast.add({ label: 'Added to bag', time: 2500 });
      };

      const isAdding = !getIsSelected(product.product_id);
      const success = toggleProduct(compareProduct, {
        addToCart: addFromCompareModal,
        view: trackModalProductClick,
      });
      if (!success) {
        toast.add({
          label: `Maximum ${MAX_COMPARE} products for comparison`,
          time: 2500,
        });
      } else if (isAdding) {
        setCompareActive(true);
        trackAddedProductToCompare(getProductCount() + 1, compareTrackingProps);
      }
    }, [
      compareProduct,
      toggleProduct,
      setCompareActive,
      getIsSelected,
      getProductCount,
      toast,
      product,
      onSdkProductClick,
      hasPreviousStoreProducts,
      navigate,
      submit,
      defaultWeight,
      formArgs,
    ]);

    // Tapping the product fires both DM (smart-sort) click tracking via
    // `selected` and the Mixpanel listing-tap event. Shared so the image and
    // the product-name links stay in sync.
    const handleProductTap = useCallback(() => {
      selected?.();
      trackProductListingTap(
        {
          fulfillmentMode,
          product,
          reservationMode: shoppingPreferences?.reservationMode,
          storeId: pinnedStore?.id ?? shoppingPreferences?.storeId,
        },
        featureFlags
      );
    }, [
      selected,
      fulfillmentMode,
      product,
      shoppingPreferences?.reservationMode,
      shoppingPreferences?.storeId,
      pinnedStore?.id,
      featureFlags,
    ]);

    const pdpPath = `/products/${smartProductInstance?.attributes.product_id ?? product.product_id}/${product.unique_slug ?? product.id}`;

    const modalProductInfo = {
      brand: [product.brand, product.root_subtype].filter(Boolean).join(' '),
      dosageLabel: product.dosageAmountPackInfo,
      imageUrl: product.image_urls?.[0],
      // Base (fallback) potency
      potencyLabel: product.potencyLabResults,
      productName: product.name,
    };

    return (
      <>
        <BagTransferModal
          open={showWarningModal}
          onConfirm={confirmStartNewBag}
          onRequestClose={handleCartWarningClose}
          newBag={newCart}
        />

        {/* Either regular multi-weight-select modal or layered when comparing products */}
        {product.hasMultipleWeights && (
          <ClientOnly>
            {() => (
              <AddToBagModal
                {...modalProductInfo}
                disabledWeights={disabledWeights}
                isAddingToCart={isAddingToCart}
                isLayered={weightModal?.layered ?? false}
                onAddToBag={handleMultiWeightAddToBag}
                onRequestClose={() => setWeightModal(null)}
                onWeightChange={onWeightChange}
                open={Boolean(weightModal)}
                weights={weights}
              />
            )}
          </ClientOnly>
        )}

        <ProductCard
          ref={ref}
          {...cardProps}
          cardBorder={theme.bloom?.product_cards?.card_border}
          cardSize={theme.bloom?.product_cards?.card_size}
          ctaIcon={theme.bloom?.product_cards?.cta_icon}
          ctaStyle={theme.bloom?.product_cards?.cta_style}
          hover={theme.bloom?.product_cards?.hover}
          renderCompareToggle={
            compareProduct
              ? ({ cardSize, placement }) => (
                  <MenuCompareToggle
                    cardSize={cardSize}
                    onToggle={handleCompareToggle}
                    placement={placement}
                    productId={product.product_id}
                    productName={product.name}
                  />
                )
              : undefined
          }
          onImpress={() => smartProductInstance?.impress()}
          onProductClick={handleProductTap}
          onSelectWeight={() => {
            onSdkProductClick(false);
            setWeightModal({ layered: false });
          }}
          renderLink={({
            'aria-hidden': ariaHidden,
            children,
            className,
            onClick,
            tabIndex,
          }) => (
            <BloomLink
              to={pdpPath}
              aria-hidden={ariaHidden}
              className={className}
              onClick={onClick}
              style={className ? undefined : { textDecoration: 'none' }}
              tabIndex={tabIndex}
            >
              {children}
            </BloomLink>
          )}
          renderProductCategoryLabel={({ className }) => (
            <ProductCategoryLabel
              category={product.category}
              className={className}
              kind={product.kind}
            />
          )}
          renderAddToBag={(button) => (
            <Form
              name={`${product.id}-add-to-cart`}
              className={styles['productCardAddToCartForm']}
              onSubmit={handleSingleWeightAddToBag}
              {...formArgs}
            >
              <input type="hidden" name="productId" value={product.id} />
              <input type="hidden" name="priceId" value={defaultWeight} />
              <input type="hidden" name="count" value={1} />
              {button}
            </Form>
          )}
        />
      </>
    );
  }
);
